import { BareActionContext, getStoreBuilder } from "vuex-typex";
import * as Models from "../models";
import { RootState } from "@/store";
import * as TranslationService from "@/services/Translations";
import http from "@/services/Http";

export interface ResellerOrderCheckoutState {
    apiUrl: string;
    paymentWidget: string;
    paymentWidgetLoading: boolean;
    resellers: Models.Reseller[];
    orderInfo: Models.OrderInfo;
    paymentError: string;
    selectedReseller: Models.Reseller;
    translations: Models.TranslationModel;
}

const initialState: ResellerOrderCheckoutState = {
    apiUrl: "/api/resellerordercheckout",
    paymentWidget: "",
    paymentWidgetLoading: false,
    paymentError: "",
    resellers: [],
    orderInfo: {} as Models.OrderInfo,
    selectedReseller: {} as Models.Reseller,
    translations: TranslationService.getTranslations<Models.TranslationModel>(),
};

const store = getStoreBuilder<RootState>().module("resllerOrder", initialState);

function apiUrl(state: ResellerOrderCheckoutState) {
    return state.apiUrl;
}

function resellers(state: ResellerOrderCheckoutState) {
    return state.resellers;
}

function orderInfo(state: ResellerOrderCheckoutState) {
    return state.orderInfo;
}

function selectedReseller(state: ResellerOrderCheckoutState) {
    return state.selectedReseller;
}

function translations(state: ResellerOrderCheckoutState) {
    return state.translations;
}

function paymentWidget(state: ResellerOrderCheckoutState) {
    return state.paymentWidget;
}

function paymentError(state: ResellerOrderCheckoutState) {
    return state.paymentError;
}

// getters
const getApiUrl = store.read(apiUrl, "apiUrl");
const getRellers = store.read(resellers, "resellers");
const getOrderInfo = store.read(orderInfo, "orderInfo");
const getSelectedReller = store.read(selectedReseller, "selectedReseller");
const getTranslations = store.read(translations, "translations");
const getPaymentWidget = store.read(paymentWidget, "paymentWidget");
const getPaymentError = store.read(paymentError, "paymentError");

function commitSetResellers(state: ResellerOrderCheckoutState, payload: Models.Reseller[]) {
    state.resellers = payload;
}

function commitSetOrderInfo(state: ResellerOrderCheckoutState, payload: Models.OrderInfo) {
    state.orderInfo = payload;
}

function commitSetSelectedReseller(state: ResellerOrderCheckoutState, payload: Models.Reseller) {
    state.selectedReseller = payload;
}

function commitSetPaymentWidget(state: ResellerOrderCheckoutState, payload: string) {
    state.paymentWidget = payload;
}

function commitSetPaymentWidgetLoading(state: ResellerOrderCheckoutState, payload: boolean) {
    state.paymentWidgetLoading = payload;
}

function commitSetPaymentError(state: ResellerOrderCheckoutState, payload: string) {
    state.paymentError = payload;
}

// actions
async function setResellers(context: BareActionContext<ResellerOrderCheckoutState, RootState>, model: Models.Reseller[]) {
    commitSetResellers(context.state, model);
}

async function setOrderInfo(context: BareActionContext<ResellerOrderCheckoutState, RootState>, model: Models.OrderInfo) {
    commitSetOrderInfo(context.state, model);
}

async function setSelectedReseller(context: BareActionContext<ResellerOrderCheckoutState, RootState>, model: Models.Reseller) {
    commitSetSelectedReseller(context.state, model);
    if (model.id) {
        commitSetPaymentWidget(context.state, "");
        commitSetPaymentError(context.state, "");
        commitSetPaymentWidgetLoading(context.state, true);
        http.get<any>(getApiUrl() + "/paymentwidget/" + model.id)
        .then(res => commitSetPaymentWidget(context.state, res.widget))
        .catch(ex => commitSetPaymentError(context.state, ex.message))
        .finally(() => commitSetPaymentWidgetLoading(context.state, false));
    }
}

export const resellerOrderCheckoutStore = {
    get paymentWidget() { return getPaymentWidget(); },
    get apiUrl() { return getApiUrl(); },
    get resellers() { return getRellers(); },
    get selectedReseller() { return getSelectedReller(); },
    get translations() { return getTranslations(); },
    get paymentError() { return getPaymentError(); },
    get orderInfo() { return getOrderInfo(); },
    setResellers: store.dispatch(setResellers, "setResellers"),
    setSelectedReseller: store.dispatch(setSelectedReseller, "setSelectedReseller"),
    setOrderInfo: store.dispatch(setOrderInfo, "setOrderInfo"),
};
